import { createContext, provide } from 'component-register';
import html from 'solid-js/html';
import { onCleanup, onMount, splitProps, mergeProps } from 'solid-js';
import { customShadowlessElement } from '@brytdesigns/web-component-core/utils';

import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.177.0/+esm';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.177.0/examples/jsm/loaders/GLTFLoader.js/+esm';

function initializeSceneContext(props) {
  const [_internal, _state] = splitProps(props, ['root']);
  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(40, _internal.root.clientWidth / _internal.root.clientHeight, 1, 1000);
  camera.position.z = 1;

  return {
    scene,
    camera,
  };
}

const SceneContext = createContext(initializeSceneContext);

const ThreeScene = (props, { element }) => {
  element.style.display = 'block';

  const contextState = mergeProps(props, { root: element });
  let canvas;

  const { scene, camera } = provide(SceneContext, contextState, element);

  onMount(() => {
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const intersection = new THREE.Vector3();

    const spotLightPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(100, 100),
      new THREE.MeshBasicMaterial({ color: 0x0000ff, visible: false }),
    );
    scene.add(spotLightPlane);

    let modelGroup = new THREE.Group();
    let model = null;

    window.addEventListener('resize', () => {
      camera.aspect = element.clientWidth / element.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(element.clientWidth, element.clientHeight);
    });

    const section = canvas.closest('section');
    section.addEventListener('mousemove', (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      mouse.x = (x - 0.5) * 2;
      mouse.y = (y - 0.5) * 2;

      raycaster.setFromCamera(mouse, camera);
    });

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true });
    renderer.setSize(element.clientWidth, element.clientHeight);
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(window.devicePixelRatio);

    camera.aspect = element.clientWidth / element.clientHeight;
    camera.updateProjectionMatrix();

    camera.position.z = 2;

    const pointLight = new THREE.PointLight(0xffffff, 4, 0);
    pointLight.position.set(0, 1, 1);
    scene.add(pointLight);

    const loader = new GLTFLoader();
    loader.load(
      props.model,
      (gltf) => {
        model = gltf.scene;

        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxAxis = Math.max(size.x, size.y, size.z);
        model.scale.multiplyScalar(1.25 / maxAxis);

        model.rotation.x = 300.25;
        model.rotation.y = -600.5;
        model.rotation.z = 600;
        model.updateMatrixWorld(true);
        model.position.sub(center);

        const pivot = new THREE.Group();
        pivot.add(model);

        modelGroup.add(pivot);
        scene.add(modelGroup);

        const box2 = new THREE.Box3().setFromObject(pivot);
        const center2 = box2.getCenter(new THREE.Vector3());

        pivot.position.sub(center2);
      },
      undefined,
      (error) => {
        console.error('Error loading model:', error);
      },
    );

    function animate() {
      requestAnimationFrame(animate);

      if (model) {
        raycaster.setFromCamera(mouse, camera);
        raycaster.ray.intersectPlane(plane, intersection);
        const maxTilt = 0.05;

        const targetY = intersection.x * 0.1; // yaw
        const targetX = intersection.y * 0.1; // pitch
        const targetZ = (intersection.x - intersection.y) * 0.05; // roll

        modelGroup.rotation.y += (targetY - modelGroup.rotation.y) * 0.1;
        modelGroup.rotation.x += (targetX - modelGroup.rotation.x) * 0.1;
        modelGroup.rotation.z += (targetZ - modelGroup.rotation.z) * 0.1;
      }

      renderer.render(scene, camera);
    }
    animate();

    onCleanup(() => {
      renderer.dispose();
    });
  });

  return html` <canvas style="width: 100%; height: 100%;" ref=${(el) => (canvas = el)}></canvas> `;
};

customShadowlessElement('three-scene', { model: '' }, ThreeScene);
